/**
 * CausticSea — Phase 3's animated background, looking *down* into a
 * sun-dappled Cambrian shallow sea. World-anchored Mesh (same pattern as
 * Phase 2's BiofieldMesh) so camera scrolling is handled by the parent
 * container's transform and the caustic pattern stays pinned to fixed
 * world points.
 *
 * Layers:
 *   1. Warm depth gradient (top = golden sunlight surface;
 *      bottom = deep teal sand-bed)
 *   2. Dual-octave voronoi-ish caustic "web" — bright lacy veins on the
 *      sand bed, animated to shimmer
 *   3. Vertical sun-ray shafts (slow horizontal drift) reading as
 *      god-rays from the surface above
 *   4. Sparse sand speckles
 *   5. Soft warm halo around the player (uPlayerPos) — reads as the
 *      player carrying the brightest spot of light wherever they go
 *
 * No vignette here — Phase 3 doesn't have a minimap framing it, and
 * the screen-edge HUD elements already do the job of bounding the view.
 */

import { Mesh, MeshGeometry, Shader, GlProgram, Texture } from 'pixi.js';

const vertex = /* glsl */ `
  in vec2 aPosition;

  uniform mat3 uProjectionMatrix;
  uniform mat3 uWorldTransformMatrix;
  uniform mat3 uTransformMatrix;

  out vec2 vWorldPos;

  void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vWorldPos = aPosition;
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  in vec2 vWorldPos;
  out vec4 finalColor;

  uniform float uTime;
  uniform vec2 uWorldSize;
  uniform vec2 uPlayerPos;

  float hash1(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  vec2 hash2(vec2 p) {
    return vec2(
      fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453),
      fract(sin(dot(p, vec2(269.5,  183.3))) * 43758.5453)
    );
  }

  float valueNoise(vec2 uv) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float a = hash1(i);
    float b = hash1(i + vec2(1.0, 0.0));
    float c = hash1(i + vec2(0.0, 1.0));
    float d = hash1(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  // Voronoi-distance — returns the distance from the sample point to
  // its nearest cell centre. Two layers of this added together produce
  // the lacy caustic web pattern you see on shallow seabeds.
  float voronoi(vec2 uv, float t) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float best = 8.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 g = vec2(float(x), float(y));
        vec2 p = hash2(i + g);
        // Animate each cell centre on a small circular orbit. Different
        // phases per cell give the shimmer organic, non-uniform motion.
        p = 0.5 + 0.5 * sin(t * 0.6 + 6.2831 * p);
        vec2 r = g + p - f;
        float d = dot(r, r);
        best = min(best, d);
      }
    }
    return sqrt(best);
  }

  void main() {
    vec2 wp = vWorldPos;
    float t = uTime;

    // Vertical depth gradient. Top = golden surface light, bottom = deep teal.
    float worldY = clamp(wp.y / max(uWorldSize.y, 1.0), 0.0, 1.0);
    vec3 topCol = vec3(0.18, 0.42, 0.46);  // turquoise, sun-side
    vec3 botCol = vec3(0.04, 0.16, 0.22);  // deep teal, sand-shadow
    vec3 col = mix(topCol, botCol, smoothstep(0.0, 1.0, worldY));

    // Dual-octave caustic web. Two voronoi layers at different scales
    // and animation phases. The web is the "negative space" — bright
    // where the voronoi distance is small (close to cell centres).
    vec2 cuv = wp / 220.0;
    float v1 = voronoi(cuv,            t);
    float v2 = voronoi(cuv * 2.2 + 7.0, t * 1.15);
    // Crisp the bright edges. Lower bound = how thick the lacy web is.
    float caustic = smoothstep(0.55, 0.05, v1) + smoothstep(0.55, 0.05, v2) * 0.55;
    // Caustic is brightest at the surface, dimmer in the deep.
    float depthFade = mix(1.0, 0.35, smoothstep(0.0, 1.0, worldY));
    vec3 causticCol = vec3(1.00, 0.92, 0.62); // golden surface light
    col += causticCol * caustic * 0.35 * depthFade;

    // Vertical god-ray shafts. Few, wide, slow horizontal drift. Most
    // visible near the top where the gradient is brightest.
    float rayUv = wp.x * 0.0018 + sin(wp.y * 0.0009 + t * 0.05) * 0.6 - t * 0.02;
    float rays = sin(rayUv * 2.0) * 0.5 + sin(rayUv * 3.7 + 1.3) * 0.5;
    rays = smoothstep(0.65, 1.0, rays);
    float rayMask = smoothstep(1.0, 0.0, worldY); // strongest at top
    col += vec3(1.0, 0.95, 0.70) * rays * rayMask * 0.18;

    // Sparse sand speckles. Small high-frequency noise picked above a
    // threshold — looks like reflective grit in the sand.
    vec2 sp = wp / 32.0;
    float speckle = step(0.96, valueNoise(sp));
    col += vec3(1.0, 0.95, 0.80) * speckle * 0.18 * (0.4 + depthFade * 0.6);

    // Warm halo following the player.
    float dPlayer = distance(wp, uPlayerPos);
    float playerPulse = 0.65 + 0.35 * sin(t * 1.2);
    col += vec3(1.0, 0.78, 0.40) * exp(-dPlayer / 260.0) * playerPulse * 0.18;

    // Translucent so the underlying seafloor tile reads through —
    // the mesh acts as shimmer + warm player halo, not a base color.
    // (Mesh shaders don't auto-apply DisplayObject.alpha, so bake it here.)
    finalColor = vec4(col, 0.45);
  }
`;

export class CausticSeaMesh extends Mesh {
  constructor(worldW: number, worldH: number) {
    const positions = new Float32Array([
      0,      0,
      worldW, 0,
      worldW, worldH,
      0,      worldH,
    ]);
    const uvs = new Float32Array([
      0, 0,
      1, 0,
      1, 1,
      0, 1,
    ]);
    const indices = new Uint32Array([0, 1, 2, 0, 2, 3]);

    const geometry = new MeshGeometry({ positions, uvs, indices });
    const glProgram = GlProgram.from({ vertex, fragment, name: 'caustic-sea-mesh' });
    const shader = new Shader({
      glProgram,
      resources: {
        uniforms: {
          uTime: { value: 0, type: 'f32' },
          uWorldSize: { value: [worldW, worldH], type: 'vec2<f32>' },
          uPlayerPos: { value: [worldW * 0.5, worldH * 0.5], type: 'vec2<f32>' },
        },
      },
    });

    super({
      geometry,
      shader: shader as unknown as never,
      texture: Texture.WHITE,
    });
  }

  update(dt: number, playerX: number, playerY: number): void {
    const u = ((this.shader as Shader).resources.uniforms as any).uniforms;
    u.uTime += dt;
    u.uPlayerPos[0] = playerX;
    u.uPlayerPos[1] = playerY;
  }
}
